import { useState, useEffect, useRef, useMemo } from 'react'
import {
  getRoiAnalytics,
  updateRoiAssumptions,
  type RoiAnalyticsResult,
  type RoiMonth,
} from '../services/roiAnalytics'
import { logFeature } from '../services/activity'
import {
  type RoiAssumptions,
  type RoiCounts,
  DEFAULT_ASSUMPTIONS,
  ASSUMPTION_BOUNDS,
  MEDICAL_VERIFY_MINUTES,
  VISION_VERIFY_MINUTES,
  deriveRoi,
} from '../utils/roiEstimates'
import { buildRoiLegend } from '../utils/roiLegend'
import './RoiAnalytics.css'

/**
 * Analytics — the practice's value view (v2, from the approved prototype).
 *
 * Structure (metrics before visuals): North Star "Cash recovered" with a
 * stacked source bar → four KPI tiles → recovery-by-month columns + pipeline
 * health → the line-by-line receipt. No cost/ROI anywhere — many viewers see
 * this page without seeing pricing.
 *
 * Honesty contract: counts and identified dollars are measured pipeline
 * output; every dollar that applies an assumption wears an "Est." badge and
 * recalculates live from the assumptions panel. Tone: report, not pitch.
 */

type RangeKey = 'mtd' | 'last' | '3mo' | '12m'

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})
const usdK = (n: number) =>
  n >= 1000 ? `$${(Math.round(n / 100) / 10).toLocaleString('en-US')}K` : usd.format(n)

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthLabel = (period: string) => MONTH_LABELS[Number(period.slice(5, 7)) - 1] ?? period

const EMPTY_COUNTS: RoiCounts & {
  checksRun: number
  verifications: number
  identifiedCount: number
  visitsTotal: number
  visitsVerified: number
} = {
  identifiedBaseDollars: 0,
  cappableAmounts: [],
  visionCaught: 0,
  oonCaught: 0,
  inactiveCaught: 0,
  medicalVerifications: 0,
  visionVerifications: 0,
  checksRun: 0,
  verifications: 0,
  identifiedCount: 0,
  visitsTotal: 0,
  visitsVerified: 0,
}

function sumMonths(months: RoiMonth[]) {
  const t = { ...EMPTY_COUNTS, cappableAmounts: [] as number[] }
  for (const m of months) {
    t.identifiedBaseDollars += m.identifiedBaseDollars
    t.cappableAmounts.push(...m.cappableAmounts)
    t.visionCaught += m.visionCaught
    t.oonCaught += m.oonCaught
    t.inactiveCaught += m.inactiveCaught
    t.medicalVerifications += m.medicalVerifications
    t.visionVerifications += m.visionVerifications
    t.checksRun += m.checksRun
    t.verifications += m.verifications
    t.identifiedCount += m.identifiedCount
    t.visitsTotal += m.visitsTotal
    t.visitsVerified += m.visitsVerified
  }
  return t
}

/** Which of the 12 fetched months a range covers. Current month is last. */
function rangeSlice(range: RangeKey, len: number): [number, number] {
  switch (range) {
    case 'mtd': return [len - 1, len]
    case 'last': return [len - 2, len - 1]
    case '3mo': return [len - 4, len - 1]
    case '12m': return [0, len]
  }
}

// ── Tooltip ───────────────────────────────────────────────────────────

interface TipState {
  x: number
  y: number
  title: string
  rows: Array<{ name: string; value: string; swatch: string }>
  totalLabel: string | null
  total: string | null
}

const SOURCE_META = [
  { key: 'uc', name: 'Under-collection recovered', cls: 'roi-s1' },
  { key: 'vsp', name: 'VSP write-offs prevented', cls: 'roi-s2' },
  { key: 'med', name: 'Medical write-offs prevented', cls: 'roi-s3' },
] as const

/** The hero bar carries the whole total — cash sources in the blue ramp,
 *  time components in slate so cash vs. time still reads at a glance. */
const VALUE_META = [
  ...SOURCE_META,
  { key: 'rework', name: 'Denial rework avoided', cls: 'roi-s4' },
  { key: 'hours', name: 'Front-desk hours returned', cls: 'roi-s5' },
] as const

function Tooltip({ tip }: { tip: TipState | null }) {
  const ref = useRef<HTMLDivElement>(null)
  // Position after render so the box can measure itself against the viewport.
  useEffect(() => {
    const el = ref.current
    if (!el || !tip) return
    const w = el.offsetWidth
    const h = el.offsetHeight
    el.style.left = `${Math.min(tip.x + 14, window.innerWidth - w - 10)}px`
    el.style.top = `${Math.max(8, Math.min(tip.y - h - 12, window.innerHeight - h - 10))}px`
  }, [tip])
  if (!tip) return null
  return (
    <div className="roi-tip" ref={ref} role="status">
      <div className="roi-tip__title">{tip.title}</div>
      {tip.rows.map(r => (
        <div className="roi-tip__row" key={r.name}>
          <span className={`roi-tip__key ${r.swatch}`} />
          <span className="roi-tip__name">{r.name}</span>
          <span className="roi-tip__val">{r.value}</span>
        </div>
      ))}
      {tip.total !== null && (
        <div className="roi-tip__total"><span>{tip.totalLabel}</span><span>{tip.total}</span></div>
      )}
    </div>
  )
}

// ── Small charts (hand-rolled SVG — no chart dependency) ──────────────

function Sparkline({ series }: { series: number[] }) {
  const W = 110
  const H = 26
  if (series.length < 2) return null
  const min = Math.min(...series)
  const max = Math.max(...series)
  const span = max - min || 1
  const pts = series.map((v, i) => [
    3 + (i * (W - 10)) / (series.length - 1),
    H - 4 - ((v - min) / span) * (H - 8),
  ])
  const last = pts[pts.length - 1]
  return (
    <svg className="roi-spark" width={W} height={H} aria-hidden="true">
      <polyline
        points={pts.map(p => p.map(n => n.toFixed(1)).join(',')).join(' ')}
        fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
      />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill="#2563eb" stroke="#fff" strokeWidth="2" />
    </svg>
  )
}

/** Path for a column with only its top corners rounded. */
function topRoundRect(x: number, y: number, w: number, h: number, r: number): string {
  r = Math.max(0, Math.min(r, w / 2, h))
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
}

/** A tick step that lands 3–6 gridlines regardless of scale. */
function tickStep(maxV: number): number {
  const raw = maxV / 4
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))))
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (raw <= m * mag) return m * mag
  }
  return 10 * mag
}

interface TrendMonth {
  month: string
  mtd: boolean
  active: boolean
  uc: number
  vsp: number
  med: number
  cash: number
}

function TrendChart({ rows, onTip }: { rows: TrendMonth[]; onTip: (t: TipState | null) => void }) {
  const [hover, setHover] = useState<string | null>(null)
  const W = 640
  const H = 250
  const padL = 44
  const padR = 16
  const padT = 22
  const padB = 28
  const plotW = W - padL - padR
  const plotH = H - padT - padB
  const maxCash = Math.max(...rows.map(r => r.cash), 1)
  const maxV = maxCash * 1.08
  const y = (v: number) => padT + plotH - (v / maxV) * plotH
  const band = plotW / rows.length
  const colW = Math.min(52, band * 0.7)
  const step = tickStep(maxV)

  const ticks: number[] = []
  for (let t = 0; t <= maxV; t += step) ticks.push(t)

  const show = (r: TrendMonth, cx: number, cy: number) => {
    setHover(r.month)
    onTip({
      x: cx, y: cy,
      title: `${monthLabel(r.month)} ${r.month.slice(0, 4)}${r.mtd ? ' · month to date' : ''}`,
      // Itemize only the column's rendered segments (segs filter v > 0 below)
      // — a "$0" row with a swatch the column doesn't contain sends the reader
      // hunting for a missing color, the same trust failure as the hero legend
      // (#249). A no-VSP practice never sees a VSP row here either.
      rows: ([
        { name: SOURCE_META[0].name, v: r.uc, swatch: 'roi-s1' },
        { name: SOURCE_META[1].name, v: r.vsp, swatch: 'roi-s2' },
        { name: SOURCE_META[2].name, v: r.med, swatch: 'roi-s3' },
      ])
        .filter(s => s.v > 0)
        .map(s => ({ name: s.name, value: usd.format(s.v), swatch: s.swatch })),
      totalLabel: 'Cash recovered',
      total: usd.format(r.cash),
    })
  }
  const hide = () => { setHover(null); onTip(null) }

  return (
    <svg className="roi-trend" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="Stacked monthly columns of cash recovered by source. Values are in the table view.">
      {ticks.map(t => (
        <g key={t}>
          <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)}
            className={t === 0 ? 'roi-trend__baseline' : 'roi-trend__gridline'} />
          <text x={padL - 6} y={y(t) + 3} textAnchor="end" className="roi-trend__tick">
            {t === 0 ? '0' : t >= 1000 ? `${t / 1000}K` : t}
          </text>
        </g>
      ))}
      {rows.map((r, i) => {
        const x = padL + band * i + (band - colW) / 2
        let cursor = 0
        const segs = [
          { v: r.uc, cls: 'roi-trend__f1' },
          { v: r.vsp, cls: 'roi-trend__f2' },
          { v: r.med, cls: 'roi-trend__f3' },
        ].filter(s => s.v > 0)
        const isPeak = r.cash === maxCash && r.cash > 0
        return (
          <g key={r.month}
            className={`roi-trend__month${r.active ? '' : ' roi-trend__month--dim'}${hover === r.month ? ' roi-trend__month--hover' : ''}`}>
            {segs.map((s, si) => {
              const y1 = y(cursor + s.v)
              const y0 = y(cursor)
              const h = Math.max(1, y0 - y1) // color separation carries the stack — no gap lines
              cursor += s.v
              return si === segs.length - 1
                ? <path key={s.cls} d={topRoundRect(x, y1, colW, h, 4)} className={s.cls} />
                : <rect key={s.cls} x={x} y={y1} width={colW} height={h} className={s.cls} />
            })}
            <text x={x + colW / 2} y={H - 10} textAnchor="middle" className="roi-trend__label">
              {r.mtd ? `${monthLabel(r.month)}*` : monthLabel(r.month)}
            </text>
            {isPeak && (
              <text x={x + colW / 2} y={y(r.cash) - 6} textAnchor="middle" className="roi-trend__peak">
                {usdK(r.cash)}
              </text>
            )}
            <rect
              x={padL + band * i} y={padT} width={band} height={plotH}
              className="roi-trend__hit" tabIndex={0} role="img"
              aria-label={`${monthLabel(r.month)}${r.mtd ? ' (month to date)' : ''}: ${usd.format(r.cash)} cash recovered`}
              onPointerMove={e => show(r, e.clientX, e.clientY)}
              onPointerLeave={hide}
              onFocus={e => { const b = e.currentTarget.getBoundingClientRect(); show(r, b.left + b.width / 2, b.top + 40) }}
              onBlur={hide}
            />
          </g>
        )
      })}
    </svg>
  )
}

// ── Page ──────────────────────────────────────────────────────────────

export default function RoiAnalytics() {
  const [range, setRange] = useState<RangeKey>('3mo')
  const [clinicId, setClinicId] = useState<string>('')
  const [data, setData] = useState<RoiAnalyticsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [assumptions, setAssumptions] = useState<RoiAssumptions>(DEFAULT_ASSUMPTIONS)
  const [assumeOpen, setAssumeOpen] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const [tip, setTip] = useState<TipState | null>(null)

  // Monotonic request id — a slow earlier response must not clobber the
  // result of a later clinic selection.
  const reqSeq = useRef(0)

  async function load(clinic: string) {
    const req = ++reqSeq.current
    setLoading(true)
    setError(null)
    try {
      const result = await getRoiAnalytics(clinic || null)
      if (reqSeq.current !== req) return
      setData(result)
      setAssumptions(result.assumptions)
    } catch {
      if (reqSeq.current !== req) return
      setError('Failed to load analytics. Please try again.')
    } finally {
      if (reqSeq.current === req) setLoading(false)
    }
  }

  // Fetch once on mount; clinic changes re-fetch from the select handler.
  useEffect(() => { load(''); logFeature('roi') }, [])

  const months = useMemo(() => data?.months ?? [], [data])
  const len = months.length

  // Per-month derived cash (for the trend) + range aggregates.
  const [lo, hi] = rangeSlice(range, len)
  const trendRows: TrendMonth[] = useMemo(() => months.map((m, i) => {
    const d = deriveRoi(m, assumptions)
    return {
      month: m.month,
      mtd: i === len - 1,
      active: i >= lo && i < hi,
      uc: d.underCollectionUsd,
      vsp: d.vspWriteOffsUsd,
      med: d.medicalWriteOffsUsd,
      cash: d.cashRecoveredUsd,
    }
  }), [months, assumptions, lo, hi, len])

  // The chart shows the practice's actual history, not 12 empty slots:
  // 3–6 trailing months, growing from the first month with data. Older
  // months still count in the range totals; they're just not charted.
  const firstDataIdx = months.findIndex(m => m.checksRun > 0 || m.visitsTotal > 0)
  const monthsWithData = firstDataIdx === -1 ? 0 : len - firstDataIdx
  const chartCount = Math.max(3, Math.min(6, monthsWithData))
  const chartRows = trendRows.slice(Math.max(0, len - chartCount))

  const counts = useMemo(() => sumMonths(months.slice(lo, hi)), [months, lo, hi])
  // Memoized: setTip fires per pointer sample over the bar/trend, and deriveRoi
  // reduces cappableAmounts — whole-history for everDerived below.
  const derived = useMemo(() => deriveRoi(counts, assumptions), [counts, assumptions])

  // Prior-period delta on the hero (total value) — only where a same-length
  // prior window exists and delivered anything (a $0 baseline makes every
  // percentage meaningless).
  let deltaPct: number | null = null
  if (range === 'last' || range === '3mo') {
    const span = hi - lo
    const priorLo = lo - span
    if (priorLo >= 0) {
      const prior = deriveRoi(sumMonths(months.slice(priorLo, lo)), assumptions)
      if (prior.totalValueUsd > 0) {
        deltaPct = Math.round(((derived.totalValueUsd - prior.totalValueUsd) / prior.totalValueUsd) * 100)
      }
    }
  }

  const successRate = counts.checksRun > 0 ? (counts.verifications / counts.checksRun) * 100 : null
  const coverage = counts.visitsTotal > 0 ? Math.round((counts.visitsVerified / counts.visitsTotal) * 100) : null
  const minPerCheck = counts.verifications > 0 ? (derived.hoursReturned * 60) / counts.verifications : null
  const denialsTotal = counts.inactiveCaught + counts.oonCaught + counts.visionCaught
  const fixablePct = denialsTotal > 0 ? Math.round((counts.inactiveCaught / denialsTotal) * 100) : null

  // Range labels, from real month keys + the data-through day.
  const throughDay = data ? Number(data.dataThrough.slice(8, 10)) : 0
  const cur = len ? months[len - 1].month : ''
  const rangeLabel = (() => {
    if (!len) return ''
    switch (range) {
      case 'mtd': return `${monthLabel(cur)} 1–${throughDay} (month to date)`
      case 'last': return `${monthLabel(months[len - 2].month)} ${months[len - 2].month.slice(0, 4)}`
      case '3mo': return `${monthLabel(months[len - 4].month)} – ${monthLabel(months[len - 2].month)}`
      case '12m': return 'last 12 months'
    }
  })()
  const clinicName = clinicId ? (data?.clinics.find(c => c.clinicId === clinicId)?.name ?? '') : ''
  const scopeLabel = `· ${rangeLabel}${clinicName ? ` · ${clinicName}` : ''}`

  const totalEver = useMemo(() => sumMonths(months), [months])
  const isAdmin = data?.role === 'admin'

  // Hero bar segments — every component of the total (hidden entirely at $0).
  // `ever` is the category's value across the practice's full fetched history:
  // the legend's applicability signal (#249) — a category with no history at
  // all (a non-VSP practice's VSP line) gets no chip and no tooltip row, so
  // the legend never advertises a color the bar cannot contain. Deliberately
  // dollars, not raw counts: a category a zeroed assumption rules out can
  // never put a segment in the bar either, so it earns no chip — the receipt
  // below still itemizes it at $0. Memoized (with `derived` above) because
  // the tooltip's pointermove re-renders the page per pointer sample.
  const everDerived = useMemo(() => deriveRoi(totalEver, assumptions), [totalEver, assumptions])
  const { barSegs, legendEntries, tipRows } = useMemo(() => {
    const barSegs = [
      { v: derived.underCollectionUsd, ever: everDerived.underCollectionUsd, meta: VALUE_META[0] },
      { v: derived.vspWriteOffsUsd, ever: everDerived.vspWriteOffsUsd, meta: VALUE_META[1] },
      { v: derived.medicalWriteOffsUsd, ever: everDerived.medicalWriteOffsUsd, meta: VALUE_META[2] },
      { v: derived.denialReworkUsd, ever: everDerived.denialReworkUsd, meta: VALUE_META[3] },
      { v: derived.hoursReturnedUsd, ever: everDerived.hoursReturnedUsd, meta: VALUE_META[4] },
    ]
    const legendEntries = buildRoiLegend(barSegs.map(s => ({
      key: s.meta.key, name: s.meta.name, cls: s.meta.cls, periodUsd: s.v, everUsd: s.ever,
    })))
    // The tooltip itemizes the BAR: exactly the segments with value, in bar
    // order — a "$0.00" row would advertise a color the bar doesn't contain
    // (#249); the applicable-but-zero story lives on the muted legend chip.
    const valueByKey = new Map<string, number>(barSegs.map(s => [s.meta.key, s.v]))
    const tipRows = legendEntries
      .filter(e => !e.zeroThisPeriod)
      .map(e => ({ name: e.name, value: usd.format(valueByKey.get(e.key) ?? 0), swatch: e.cls }))
    return { barSegs, legendEntries, tipRows }
  }, [derived, everDerived])
  const showBarTip = (x: number, y: number) => setTip({
    x, y,
    title: rangeLabel + (clinicName ? ` · ${clinicName}` : ''),
    rows: tipRows,
    totalLabel: 'Total value',
    total: usd.format(derived.totalValueUsd),
  })

  function setAssumption(key: keyof RoiAssumptions, raw: number, scale = 1) {
    const v = raw / scale
    const { min, max } = ASSUMPTION_BOUNDS[key]
    if (!Number.isFinite(v) || v < min || v > max) return
    setAssumptions(a => ({ ...a, [key]: v }))
    setSaveState('idle')
  }

  async function saveAssumptions() {
    setSaveState('saving')
    try {
      await updateRoiAssumptions(assumptions)
      setSaveState('saved')
    } catch {
      setSaveState('failed')
    }
  }

  const estTag = (title: string) => <span className="roi-badge" title={title}>Est.</span>
  const capTag = <span className="roi-badge roi-badge--cap">Time, not cash</span>

  return (
    <main className="dashboard-main">
      <div className="roi-analytics" onScroll={() => setTip(null)}>
        <div className="roi-analytics__header">
          <div>
            <h2 className="roi-analytics__title">Analytics</h2>
            <p className="roi-analytics__subtitle">What VitalRev has delivered for your practice</p>
          </div>
        </div>

        {loading && <p className="roi-analytics__status">Loading analytics…</p>}
        {error && (
          <div className="error-banner">
            <p className="error-banner__text">{error}</p>
          </div>
        )}

        {!loading && !error && data && totalEver.checksRun === 0 && (
          <p className="roi-analytics__status">
            No eligibility checks yet. Once checks start running, your value
            numbers build here automatically.
          </p>
        )}

        {!loading && !error && data && totalEver.checksRun > 0 && (
          <>
            <div className="roi-filters" role="group" aria-label="Filters">
              <span className="roi-window">
                {([
                  ['mtd', 'This month'],
                  ['last', len ? monthLabel(months[len - 2].month) : 'Last month'],
                  ['3mo', 'Last 3 months'],
                  ['12m', 'Last 12 months'],
                ] as Array<[RangeKey, string]>).map(([key, label]) => (
                  <button
                    key={key}
                    className="roi-window__option"
                    aria-pressed={range === key}
                    onClick={() => setRange(key)}
                  >
                    {label}
                  </button>
                ))}
              </span>
              {isAdmin && data.clinics.length > 1 && (
                <select
                  className="roi-clinic-select"
                  aria-label="Clinic"
                  value={clinicId}
                  onChange={e => { setClinicId(e.target.value); load(e.target.value) }}
                >
                  <option value="">All clinics</option>
                  {data.clinics.map(c => (
                    <option key={c.clinicId} value={c.clinicId}>{c.name}</option>
                  ))}
                </select>
              )}
              <span className="roi-filters__through">
                Data through {new Date(`${data.dataThrough}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>

            {data.truncated && (
              <p className="roi-analytics__truncated">
                This window exceeded the reporting cap — figures below are a floor, not a complete total.
              </p>
            )}

            {/* North Star — total value delivered */}
            <section className="roi-card roi-hero">
              <div className="roi-hero__head">
                <h3 className="roi-card__eyebrow">
                  Total value delivered {scopeLabel} {estTag('Cash recovered plus the value of staff time returned, estimated from your assumptions below — the receipt shows each line’s math.')}
                </h3>
                <span className="roi-pill-note">conservative floor</span>
              </div>
              <div className="roi-hero__value">{usd.format(derived.totalValueUsd)}</div>
              <div className="roi-hero__head">
                {deltaPct !== null && (
                  <span className="roi-delta">
                    {deltaPct >= 0 ? '▲ +' : '▼ '}{deltaPct}% vs prior period{range === '3mo' ? ' (ramp-up)' : ''}
                  </span>
                )}
                <span className="roi-hero__note">
                  cash recovered + staff time returned · measured checks × your assumptions · nothing counted twice
                </span>
              </div>
              {derived.totalValueUsd > 0 && (
                <>
                  <div className="roi-stackbar" aria-hidden="true">
                    {barSegs.filter(s => s.v > 0).map(s => (
                      <div
                        key={s.meta.key}
                        className={`roi-stackbar__seg ${s.meta.cls}`}
                        style={{ width: `${(s.v / derived.totalValueUsd) * 100}%` }}
                        onPointerMove={e => showBarTip(e.clientX, e.clientY)}
                        onPointerLeave={() => setTip(null)}
                      >
                        {s.v / derived.totalValueUsd > 0.11 ? usdK(s.v) : ''}
                      </div>
                    ))}
                  </div>
                  {/* Inventory, not schema (#249): chips exactly match the bar —
                      no-history categories are absent; applicable-but-$0 ones
                      are muted and say so instead of promising a segment. */}
                  <div className="roi-legend">
                    {legendEntries.map(s => (
                      <span
                        key={s.key}
                        className={`roi-legend__item${s.zeroThisPeriod ? ' roi-legend__item--zero' : ''}`}
                      >
                        <span className={`roi-legend__swatch ${s.cls}`} />{s.name}
                        {s.zeroThisPeriod && <span className="roi-legend__zero">$0 this period</span>}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </section>

            {/* KPI tiles — Est. badges live on the value line so labels stay
                one line and the numbers align across tiles. */}
            <section className="roi-tiles">
              <div className="roi-tile">
                <div className="roi-tile__k">Cash recovered</div>
                <div className="roi-tile__v">
                  {usd.format(derived.cashRecoveredUsd)}
                  {estTag('Measured counts × your dollar assumptions — the receipt shows each line’s math.')}
                </div>
                <div className="roi-tile__n">money you'd otherwise have lost</div>
              </div>
              <div className="roi-tile">
                <div className="roi-tile__k">Staff hours returned</div>
                <div className="roi-tile__v">
                  {Math.round(derived.hoursReturned).toLocaleString('en-US')} h
                  {estTag('Estimated: measured check counts × the hand-check time your desk would have spent.')}
                </div>
                <div className="roi-tile__n">worth {usdK(derived.hoursReturnedUsd)} at ${assumptions.frontDeskHourlyUsd}/hr</div>
                <Sparkline series={months.map(m => deriveRoi(m, assumptions).hoursReturned)} />
              </div>
              <div className="roi-tile">
                <div className="roi-tile__k">Checks completed</div>
                <div className="roi-tile__v">{counts.verifications.toLocaleString('en-US')}</div>
                <div className="roi-tile__n">
                  {successRate !== null ? `${successRate.toFixed(1)}% clean-answer rate` : 'no checks in this range'}
                </div>
              </div>
              <div className="roi-tile">
                <div className="roi-tile__k">Denials caught early</div>
                <div className="roi-tile__v">
                  {denialsTotal.toLocaleString('en-US')}
                  {estTag("VitalRev flags these before the visit — it doesn't work the denial itself, so the impact is estimated.")}
                </div>
                <div className="roi-tile__n">flagged before the patient arrived</div>
              </div>
            </section>

            <div className="roi-row2">
            {/* Recovery by month */}
            <section className="roi-card">
              <h3 className="roi-card__eyebrow">Recovery by month</h3>
              <p className="roi-card__hint">Cash recovered each month, by source.</p>
              <TrendChart rows={chartRows} onTip={setTip} />
              <p className="roi-card__hint roi-card__hint--foot">
                Dimmed months sit outside the selected range. *{monthLabel(cur)} is month-to-date (through {monthLabel(cur)} {throughDay}).
              </p>
            </section>

            {/* Pipeline health */}
            <section className="roi-card">
              <h3 className="roi-card__eyebrow">Pipeline health</h3>
              <p className="roi-card__hint">Is the engine doing its job?</p>
              <div className="roi-hgrid">
                <div className="roi-hstat">
                  <div className="roi-tile__k">Success rate</div>
                  <div className="roi-hstat__v">{successRate !== null ? `${successRate.toFixed(1)}%` : '—'}</div>
                  <Sparkline series={months.filter(m => m.checksRun > 0).map(m => (m.verifications / m.checksRun) * 100)} />
                  <div className="roi-hstat__sub">checks returning a clean result</div>
                </div>
                <div className="roi-hstat">
                  <div className="roi-tile__k">Visits covered</div>
                  <div className="roi-hstat__v">{coverage !== null ? `${coverage}%` : '—'}</div>
                  {coverage !== null && (
                    <div className="roi-meter" aria-hidden="true">
                      <div className="roi-meter__fill" style={{ width: `${coverage}%` }} />
                    </div>
                  )}
                  <div className="roi-hstat__sub">insured visits checked before arrival</div>
                </div>
                <div className="roi-hstat">
                  <div className="roi-tile__k">
                    Time saved / check {estTag('Estimated vs the hand-check time your desk would have spent.')}
                  </div>
                  <div className="roi-hstat__v">{minPerCheck !== null ? `${minPerCheck.toFixed(1)} min` : '—'}</div>
                  <div className="roi-hstat__sub">vs checking by hand</div>
                </div>
                <div className="roi-hstat">
                  <div className="roi-tile__k">
                    Fixable denials {estTag("VitalRev flags these before the visit — it doesn't work the denial itself, so the impact is estimated.")}
                  </div>
                  <div className="roi-hstat__v">{fixablePct !== null ? `${fixablePct}%` : '—'}</div>
                  <div className="roi-hstat__sub">
                    {denialsTotal > 0 ? `${counts.inactiveCaught} of ${denialsTotal} caught in time to fix` : 'no denials caught in this range'}
                  </div>
                </div>
              </div>
            </section>
            </div>

            {/* Line by line */}
            <section className="roi-card">
              <h3 className="roi-card__eyebrow">Line by line <span className="roi-card__eyebrow-scope">{scopeLabel}</span></h3>
              <p className="roi-card__hint">Every dollar traced to the checks that produced it. Nothing counted twice.</p>
              <table className="roi-receipt">
                <tbody>
                  <tr>
                    <td className="roi-receipt__item">
                      <span className="roi-swdot roi-s1" />Under-collection recovered {estTag('The identified dollars are measured, with each visit capped at your per-visit responsibility assumption below — a patient’s full remaining deductible never counts as more than one visit’s worth. The share that never collects post-visit is also your assumption.')}
                    </td>
                    <td className="roi-receipt__math">
                      {usd.format(derived.dollarsIdentifiedUsd)} identified at check-in on {counts.identifiedCount.toLocaleString('en-US')} visits (≤ {usd.format(assumptions.avgVisitResponsibilityUsd)}/visit) × {Math.round(assumptions.postVisitLeakageRate * 100)}% that never collects post-visit
                    </td>
                    <td className="roi-receipt__amount">{usd.format(derived.underCollectionUsd)}</td>
                  </tr>
                  <tr>
                    <td className="roi-receipt__item">
                      <span className="roi-swdot roi-s2" />VSP write-offs prevented {estTag('The count is measured; the written-off dollar value per visit is your assumption below.')}
                    </td>
                    <td className="roi-receipt__math">
                      {counts.visionCaught.toLocaleString('en-US')} ineligible vision patients caught pre-visit × {usd.format(assumptions.vspWriteOffUsd)}
                    </td>
                    <td className="roi-receipt__amount">{usd.format(derived.vspWriteOffsUsd)}</td>
                  </tr>
                  <tr>
                    <td className="roi-receipt__item">
                      <span className="roi-swdot roi-s3" />Medical write-offs prevented {estTag('The count is measured; the written-off dollar value per claim is your assumption below.')}
                    </td>
                    <td className="roi-receipt__math">
                      {counts.oonCaught.toLocaleString('en-US')} confirmed out-of-network caught pre-visit × {usd.format(assumptions.medicalWriteOffUsd)}
                    </td>
                    <td className="roi-receipt__amount">{usd.format(derived.medicalWriteOffsUsd)}</td>
                  </tr>
                  <tr className="roi-receipt__subtotal">
                    <td className="roi-receipt__item">Cash recovered</td>
                    <td className="roi-receipt__math">under-collection + write-offs prevented — money you'd otherwise lose</td>
                    <td className="roi-receipt__amount">{usd.format(derived.cashRecoveredUsd)}</td>
                  </tr>
                  <tr className="roi-receipt__capacity roi-receipt__section-start">
                    <td className="roi-receipt__item">
                      <span className="roi-swdot roi-s4" />Denial rework avoided {capTag} {estTag('The count is measured; rework minutes and rate are your assumptions below.')}
                    </td>
                    <td className="roi-receipt__math">
                      {counts.inactiveCaught.toLocaleString('en-US')} inactive policies caught × {assumptions.denialReworkMinutes} min at ${assumptions.frontDeskHourlyUsd}/hr
                    </td>
                    <td className="roi-receipt__amount">{usd.format(derived.denialReworkUsd)}</td>
                  </tr>
                  <tr className="roi-receipt__capacity">
                    <td className="roi-receipt__item">
                      <span className="roi-swdot roi-s5" />Front-desk hours returned {capTag} {estTag('Measured check counts × the hand-check time your desk would have spent.')}
                    </td>
                    <td className="roi-receipt__math">
                      {counts.medicalVerifications.toLocaleString('en-US')} medical × {MEDICAL_VERIFY_MINUTES} min + {counts.visionVerifications.toLocaleString('en-US')} vision × {VISION_VERIFY_MINUTES} min, at ${assumptions.frontDeskHourlyUsd}/hr
                    </td>
                    <td className="roi-receipt__amount">{usd.format(derived.hoursReturnedUsd)}</td>
                  </tr>
                  <tr className="roi-receipt__subtotal">
                    <td className="roi-receipt__item">Staff time returned {capTag}</td>
                    <td className="roi-receipt__math">rework avoided + hours returned, valued at your rate</td>
                    <td className="roi-receipt__amount">{usd.format(derived.denialReworkUsd + derived.hoursReturnedUsd)}</td>
                  </tr>
                  <tr className="roi-receipt__grand">
                    <td className="roi-receipt__item">Total value</td>
                    <td className="roi-receipt__math">
                      {usd.format(derived.cashRecoveredUsd)} cash recovered + {usd.format(derived.denialReworkUsd + derived.hoursReturnedUsd)} staff time returned
                    </td>
                    <td className="roi-receipt__amount">{usd.format(derived.totalValueUsd)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="roi-foot">
                Rows marked <span className="roi-badge">Est.</span> apply your practice's average dollar
                amounts to <em>measured</em> check counts — adjust the assumptions any time. Hours returned
                aren't cash; they're front-desk time moved off payer portals onto optical sales and recall.
              </p>
              <button
                className="roi-assume-btn roi-assume-btn--reveal"
                aria-expanded={assumeOpen}
                onClick={() => setAssumeOpen(o => !o)}
              >
                Adjust assumptions
              </button>
              {assumeOpen && (
                <div className="roi-assumptions">
                  <div className="roi-af">
                    <label htmlFor="roi-a-rate">Staff loaded rate ($/hr)</label>
                    <input id="roi-a-rate" type="number" min={ASSUMPTION_BOUNDS.frontDeskHourlyUsd.min} max={ASSUMPTION_BOUNDS.frontDeskHourlyUsd.max} step={1}
                      value={assumptions.frontDeskHourlyUsd}
                      onChange={e => setAssumption('frontDeskHourlyUsd', e.target.valueAsNumber)} />
                  </div>
                  <div className="roi-af">
                    <label htmlFor="roi-a-leak" title="Of the patient balances VitalRev puts on the desk's screen pre-visit, the share that would never collect once the patient leaves.">Never collects post-visit (%)</label>
                    <input id="roi-a-leak" type="number" min={0} max={100} step={5}
                      value={Math.round(assumptions.postVisitLeakageRate * 100)}
                      onChange={e => setAssumption('postVisitLeakageRate', e.target.valueAsNumber, 100)} />
                  </div>
                  <div className="roi-af">
                    <label htmlFor="roi-a-visit" title="The most one visit's identified patient responsibility can count for. Caps deductible-plan visits — the payer reports the patient's full remaining deductible, but the desk can only collect one visit's worth.">Per-visit responsibility cap ($)</label>
                    <input id="roi-a-visit" type="number" min={ASSUMPTION_BOUNDS.avgVisitResponsibilityUsd.min} max={ASSUMPTION_BOUNDS.avgVisitResponsibilityUsd.max} step={10}
                      value={assumptions.avgVisitResponsibilityUsd}
                      onChange={e => setAssumption('avgVisitResponsibilityUsd', e.target.valueAsNumber)} />
                  </div>
                  <div className="roi-af">
                    <label htmlFor="roi-a-vsp">VSP visit written off ($)</label>
                    <input id="roi-a-vsp" type="number" min={0} max={ASSUMPTION_BOUNDS.vspWriteOffUsd.max} step={5}
                      value={assumptions.vspWriteOffUsd}
                      onChange={e => setAssumption('vspWriteOffUsd', e.target.valueAsNumber)} />
                  </div>
                  <div className="roi-af">
                    <label htmlFor="roi-a-med">Medical claim written off ($)</label>
                    <input id="roi-a-med" type="number" min={0} max={ASSUMPTION_BOUNDS.medicalWriteOffUsd.max} step={5}
                      value={assumptions.medicalWriteOffUsd}
                      onChange={e => setAssumption('medicalWriteOffUsd', e.target.valueAsNumber)} />
                  </div>
                  <div className="roi-af">
                    <label htmlFor="roi-a-rework">Minutes to rework a denial</label>
                    <input id="roi-a-rework" type="number" min={0} max={ASSUMPTION_BOUNDS.denialReworkMinutes.max} step={5}
                      value={assumptions.denialReworkMinutes}
                      onChange={e => setAssumption('denialReworkMinutes', e.target.valueAsNumber)} />
                  </div>
                  <div className="roi-assumptions__note">
                    <span className="roi-assumptions__hint">
                      Every estimated dollar on this page recalculates as you type. Counts stay measured.
                      {!isAdmin && ' Changes here preview only — an admin can save them practice-wide.'}
                    </span>
                    {isAdmin && (
                      <span className="roi-assumptions__actions">
                        {saveState === 'saved' && <span className="roi-save-note">Saved.</span>}
                        {saveState === 'failed' && <span className="roi-save-note roi-save-note--err">Save failed — try again.</span>}
                        <button className="roi-save-btn" onClick={saveAssumptions} disabled={saveState === 'saving'}>
                          {saveState === 'saving' ? 'Saving…' : 'Save for the practice'}
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </section>

            <p className="roi-foot roi-foot--page">
              Collection always happens at your front desk — VitalRev counts a dollar only when the check
              that protected it actually ran.
            </p>
          </>
        )}
      </div>
      <Tooltip tip={tip} />
    </main>
  )
}
